import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import axios from "axios";

export default function BlogList() {
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    axios
      .get("https://api.livestockify.com/api/admin/blogs")
      .then((res) => {
        const mappedBlogs = res.data.map((b) => ({
          id: b.id,
          title: b.blogTitle,
          image: b.imageURL,
          views: 0,
          blogLink: b.blogLink,
        }));

        setBlogs(mappedBlogs);
      })
      .catch((err) => {
        console.error("Failed to fetch blogs:", err);
      });
  }, []);

  /* ================= SEARCH + SORT ================= */
  const filteredBlogs = blogs
    .filter((blog) =>
      blog.title?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sort === "newest") return b.id - a.id;
      return a.id - b.id;
    });

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `https://api.livestockify.com/api/admin/blogs/${id}`
      );
      setBlogs((prev) => prev.filter((b) => b.id !== id));
      setDeleteId(null);
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  /* ================= VIEW ================= */
  const handleView = async (e, blog) => {
    e.preventDefault();

    try {
      await axios.get(
        `https://api.livestockify.com/api/admin/blogs/${blog.id}/view`
      );

      setBlogs((prev) =>
        prev.map((b) =>
          b.id === blog.id ? { ...b, views: b.views + 1 } : b
        )
      );

      window.open(blog.blogLink, "_blank");
    } catch {
      window.open(blog.blogLink, "_blank");
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      <div className="flex-1 bg-white rounded-l-2xl p-4 md:p-6">
        {/* ================= HEADER ================= */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h2 className="text-lg font-semibold">Blogs</h2>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
            {/* SEARCH */}
            <input
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border rounded-md px-4 py-2 text-sm w-full sm:w-64"
            />

            {/* SORT */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-sm font-medium">Sort By</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>

            {/* ADD BLOG */}
            <button
              onClick={() => navigate("/add-blog")}
              className="border rounded-md px-4 py-2 text-sm font-medium w-full sm:w-auto"
            >
              + Add Blog
            </button>
          </div>
        </div>

        {/* ================= BLOG CARDS ================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {filteredBlogs.map((blog) => (
            <div
              key={blog.id}
              onClick={(e) => handleView(e, blog)}
              className="relative rounded-xl overflow-hidden shadow-sm bg-white cursor-pointer hover:shadow-md transition"
            >
              {/* IMAGE */}
              <img
                src={blog.image}
                alt={blog.title}
                className="h-44 w-full object-cover"
              />

              {/* 🔽 SMALL BLUR SECTION */}
              <div className="absolute bottom-0 left-0 right-0 bg-white/70 backdrop-blur-sm px-3 py-2">
                <h3 className="font-semibold text-sm text-green-700 truncate">
                  {blog.title}
                </h3>

                <p className="text-xs text-gray-600 mt-0.5">
                  👁  views: {blog.views}
                </p>

                <div className="flex justify-end gap-4 mt-2 text-sm">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/edit-blog/${blog.id}`);
                    }}
                    className="text-green-600"
                  >
                    Edit
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteId(blog.id);
                    }}
                    className="text-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredBlogs.length === 0 && (
          <p className="text-center text-gray-400 mt-10">
            No blogs found
          </p>
        )}
      </div>

      {/* ================= DELETE MODAL ================= */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-sm">
            <h3 className="text-lg font-semibold mb-3">Delete Blog</h3>

            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this blog?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="border px-4 py-1.5 rounded-md text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="bg-red-500 text-white px-4 py-1.5 rounded-md text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
